/**
 * Schedule Lines Filters Panel Component.
 *
 * Grouped into labelled sections (Order · Supplier & Warehouse · Status ·
 * Dates) so users can scan the panel faster. Supplier Site uses a
 * datalist-backed typeahead populated from getLineFilterOptions so users
 * see the real supplier names instead of having to free-type.
 */
import { useCallback, useRef, useEffect, useState, useMemo } from 'react';

import {
  ScheduleFilters,
  ACKNOWLEDGE_STATUS_OPTIONS,
  SCHEDULE_STATUS_OPTIONS,
} from './types';

import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { purchasingAPI, coreAPI } from '../../../../lib/api';
import { Search, Calendar, X, Filter, ChevronDown } from 'lucide-react';

interface ScheduleFiltersPanelProps {
  filters: ScheduleFilters;
  onFilterChange: (key: keyof ScheduleFilters, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  onHideFilters?: () => void;
}

/**
 * Debounced filter panel for schedule lines.
 * Text inputs use 500ms debounce; dropdowns and dates trigger immediately.
 */
export function ScheduleFiltersPanel({
  filters,
  onFilterChange,
  onSearch,
  onClearFilters,
  onHideFilters,
}: ScheduleFiltersPanelProps) {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [supplierOptions, setSupplierOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  // Warehouses sourced from the ``core.Warehouse`` model (admin-managed),
  // not from a hardcoded plant-derived list. Empty until the customer
  // populates Admin Setup → Warehouses.
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ id: number; name: string }>>([]);

  // Filter supplier list by the current typed value (matches the PO Lines page UX).
  const filteredSuppliers = useMemo(() => {
    const term = (filters.supplier || '').toLowerCase();
    if (!term) return supplierOptions;
    return supplierOptions.filter(s => s.name.toLowerCase().includes(term));
  }, [supplierOptions, filters.supplier]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Fetch supplier list once for the Supplier Site typeahead.
  useEffect(() => {
    let cancelled = false;
    purchasingAPI.getLineFilterOptions()
      .then((res) => {
        if (!cancelled) setSupplierOptions(res.data.suppliers || []);
      })
      .catch(() => { /* non-fatal — user can still type a supplier name */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch warehouse list from the ``core.Warehouse`` model.
  useEffect(() => {
    let cancelled = false;
    coreAPI.getWarehouses({ page_size: 200 })
      .then((res) => {
        if (!cancelled) {
          setWarehouseOptions(
            (res.data.results || []).map((w) => ({ id: w.id, name: w.name })),
          );
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  const handleTextChange = useCallback(
    (key: keyof ScheduleFilters, value: string) => {
      onFilterChange(key, value);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onSearch();
      }, 500);
    },
    [onFilterChange, onSearch]
  );

  const handleSelectChange = useCallback(
    (key: keyof ScheduleFilters, value: string) => {
      onFilterChange(key, value);
      setTimeout(() => onSearch(), 0);
    },
    [onFilterChange, onSearch]
  );

  const handleDateChange = useCallback(
    (key: keyof ScheduleFilters, value: string) => {
      onFilterChange(key, value);
      setTimeout(() => onSearch(), 0);
    },
    [onFilterChange, onSearch]
  );

  const inputCls = "w-full px-2.5 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary";
  const labelCls = "text-xs font-medium mb-1 block text-foreground";
  const sectionTitleCls = "text-[11px] font-semibold uppercase tracking-wide text-foreground mb-2";

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-base">Filter Schedule Lines</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0 pb-4">
        {/* Order identity — narrow the list to a specific PO / line / part */}
        <div>
          <div className={sectionTitleCls}>Order</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>PO Number</label>
              <input type="text" placeholder="620596..." value={filters.poNumber}
                onChange={(e) => handleTextChange('poNumber', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Line #</label>
              <input type="text" placeholder="1, 2, 3..." value={filters.lineNumber}
                onChange={(e) => handleTextChange('lineNumber', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Sched Line #</label>
              <input type="text" placeholder="1, 2, 3..." value={filters.scheduleLineNumber}
                onChange={(e) => handleTextChange('scheduleLineNumber', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Part Number</label>
              <input type="text" placeholder="Part number..." value={filters.partNumber}
                onChange={(e) => handleTextChange('partNumber', e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>

        {/* Supplier & warehouse */}
        <div>
          <div className={sectionTitleCls}>Supplier &amp; Warehouse</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
            <div className="relative">
              <label className={labelCls}>Supplier Site</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search supplier sites..."
                  value={filters.supplier}
                  onChange={(e) => {
                    handleTextChange('supplier', e.target.value);
                    setSupplierDropdownOpen(true);
                  }}
                  onFocus={() => setSupplierDropdownOpen(true)}
                  className={`${inputCls} pr-8`}
                  autoComplete="off"
                />
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                {filters.supplier && (
                  <button
                    type="button"
                    onClick={() => {
                      onFilterChange('supplier', '');
                      setTimeout(() => onSearch(), 0);
                    }}
                    className="absolute right-7 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {supplierDropdownOpen && filteredSuppliers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-auto">
                  {filteredSuppliers.slice(0, 50).map(s => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => {
                        onFilterChange('supplier', s.name);
                        setSupplierDropdownOpen(false);
                        setTimeout(() => onSearch(), 0);
                      }}
                      className="w-full px-2.5 py-1.5 text-left hover:bg-muted text-sm"
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              {supplierDropdownOpen && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setSupplierDropdownOpen(false)}
                />
              )}
            </div>
            <div>
              <label className={labelCls}>Warehouse</label>
              <select value={filters.warehouse}
                onChange={(e) => handleSelectChange('warehouse', e.target.value)} className={inputCls}>
                <option value="">All Warehouses</option>
                {warehouseOptions.map((wh) => (
                  <option key={wh.id} value={String(wh.id)}>{wh.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Status */}
        <div>
          <div className={sectionTitleCls}>Status</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Ack Status</label>
              <select value={filters.acknowledgeStatus}
                onChange={(e) => handleSelectChange('acknowledgeStatus', e.target.value)} className={inputCls}>
                <option value="">All Statuses</option>
                {ACKNOWLEDGE_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Schedule Status</label>
              <select value={filters.scheduleStatus}
                onChange={(e) => handleSelectChange('scheduleStatus', e.target.value)} className={inputCls}>
                <option value="">All Statuses</option>
                {SCHEDULE_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>On Hold</label>
              <select value={filters.onHold}
                onChange={(e) => handleSelectChange('onHold', e.target.value)} className={inputCls}>
                <option value="">All</option>
                <option value="yes">On Hold</option>
                <option value="no">Not On Hold</option>
              </select>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div>
          <div className={sectionTitleCls}>Dates</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Schedule Date From</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.scheduleDateFrom}
                  onChange={(e) => handleDateChange('scheduleDateFrom', e.target.value)}
                  className="w-full pl-8 pr-2.5 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Schedule Date To</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.scheduleDateTo}
                  onChange={(e) => handleDateChange('scheduleDateTo', e.target.value)}
                  className="w-full pl-8 pr-2.5 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Promise Date From</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.promiseDateFrom}
                  onChange={(e) => handleDateChange('promiseDateFrom', e.target.value)}
                  className="w-full pl-8 pr-2.5 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Promise Date To</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <input type="date" value={filters.promiseDateTo}
                  onChange={(e) => handleDateChange('promiseDateTo', e.target.value)}
                  className="w-full pl-8 pr-2.5 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
          </div>
        </div>

        {/* Search + Clear Buttons */}
        <div className="flex gap-2 pt-3 border-t">
          <Button size="sm" onClick={onSearch}>
            <Search className="h-3.5 w-3.5 mr-1.5" />
            Search
          </Button>
          <Button size="sm" variant="outline" onClick={onClearFilters}>
            <X className="h-3.5 w-3.5 mr-1.5" />
            Clear All
          </Button>
          {onHideFilters && (
            <Button size="sm" variant="outline" onClick={onHideFilters}>
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Hide
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
